// AddonExportPanel.tsx
// Turns a saved BIS list into the string an in-game addon reads, for one
// character (the Prio tab) or a whole roster (the guild's Loot imports tab).
//
// The two formats are not a preference, they are two different worlds. Gargul
// takes a compressed blob and can rebroadcast it to the whole raid, so one
// paste covers the night. RCLootCouncil cannot import wishlists at all (its
// only importer is loot history), so those guilds run a tooltip addon next to
// it and feed that the CSV.

import React, { useEffect, useState } from "react";

type ExportFormat = "gargul" | "csv";

const EXPORT_FORMATS: {
  id: ExportFormat;
  label: string;
  blurb: string;
  instructions: React.ReactNode;
}[] = [
  {
    id: "gargul",
    label: "Gargul",
    blurb: "Master loot, tooltips, roll windows",
    instructions: (
      <>
        Type <span className="font-mono text-white/70">/gl tmb</span> in game, paste this in, hit Import. The master
        looter can share it with the raid from the same window, so only one person needs to paste it.
      </>
    ),
  },
  {
    id: "csv",
    label: "CSV",
    blurb: "TMB Helper, Wishlist Viewer, RCLootCouncil guilds",
    instructions: (
      <>
        Paste this into your tooltip addon's config box (TMB Helper, ThatsMyBis Wishlist Viewer) and hit Parse. Re-paste
        after you change your list: nothing syncs on its own.
      </>
    ),
  },
];

export default function AddonExportPanel({
  endpointBase,
  subject,
  showToast,
}: {
  /** URL without the trailing format segment, e.g. /characters/:id/bis/export */
  endpointBase: string;
  /** Who this covers, for the summary line. */
  subject: string;
  showToast?: (message: string, type?: "success" | "error" | "info") => void;
}) {
  const [format, setFormat] = useState<ExportFormat>("gargul");
  const [cache, setCache] = useState<Record<string, { text: string; itemCount: number; characterCount: number }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const current = cache[format] ?? null;

  useEffect(() => {
    let cancelled = false;
    if (cache[format]) return;
    setBusy(true);
    setError(null);
    fetch(`${endpointBase}/${format}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setCache((prev) => ({
          ...prev,
          [format]: {
            text: format === "gargul" ? data.gargulString ?? "" : data.csv ?? "",
            itemCount: data.itemCount ?? 0,
            characterCount: data.characterCount ?? 0,
          },
        }));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Export failed");
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpointBase, format, cache]);

  const copy = async () => {
    if (!current?.text) return;
    try {
      await navigator.clipboard.writeText(current.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast?.("Could not reach the clipboard, select the text and copy it", "error");
    }
  };

  const active = EXPORT_FORMATS.find((f) => f.id === format)!;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {EXPORT_FORMATS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFormat(f.id)}
            className={`rounded-lg border px-3 py-1.5 text-left transition-colors ${
              format === f.id
                ? "border-amber-400/50 bg-amber-400/[0.14] text-amber-100"
                : "border-white/10 bg-white/[0.03] text-white/50 hover:border-white/20 hover:text-white/80"
            }`}
          >
            <span className="block text-[11px] font-bold uppercase tracking-wider">{f.label}</span>
            <span className="block text-[10px] text-white/35">{f.blurb}</span>
          </button>
        ))}
        {current ? (
          <span className="ml-auto text-[10.5px] tabular-nums text-white/35">
            {current.itemCount} item{current.itemCount === 1 ? "" : "s"}
            {current.characterCount > 1 ? ` · ${current.characterCount} characters` : ` · ${subject}`}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-[11.5px] text-red-400/95">
          {error}
        </div>
      ) : null}

      <textarea
        readOnly
        value={busy && !current ? "Building…" : current?.text ?? ""}
        onFocus={(e) => e.currentTarget.select()}
        spellCheck={false}
        className="h-28 w-full resize-none rounded-xl border border-white/10 bg-black/50 p-3 font-mono text-[10.5px] leading-relaxed text-white/60 outline-none focus:border-amber-400/40"
      />

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={copy}
          disabled={!current?.text}
          className={`rounded-xl px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40 ${
            copied
              ? "bg-emerald-500 text-neutral-950"
              : "bg-amber-400 text-neutral-950 hover:bg-amber-300"
          }`}
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <p className="min-w-0 flex-1 text-[11px] leading-relaxed text-white/40">{active.instructions}</p>
      </div>
    </div>
  );
}
